import { useMemo } from 'react';

import { Autocomplete, ListItem, StatusChip, Box } from '@cast/design-system';
import { WorkloadRecommendationStatusTypeEnum } from '@cast/types';

import { useWoopWorkloadFilters } from '../../../workload-optimization';
import {
  ERROR_STATUS_TYPE,
  LOW_CONFIDENCE_STATUS_TYPE,
  STATUS_BADGE_COLOR_MAP,
  STATUS_DISPLAY_NAME_MAP,
  STATUS_ICON_MAP,
} from '../_constants';
import { FilterableStatuses } from '../_types';
import { statusCriterion } from '../workloads-list/searchCriteria';

import { useSearchCriterion } from '@cast/shared-search-ui';

export const StatusFilter = () => {
  const { setValue, value, clear } =
    useSearchCriterion<FilterableStatuses[]>(statusCriterion);
  const filters = useWoopWorkloadFilters();

  const statusesCount = useMemo<Record<FilterableStatuses, number>>(() => {
    return {
      [WorkloadRecommendationStatusTypeEnum.APPLIED]:
        filters.data?.counts.recommendationApplied ?? 0,
      [WorkloadRecommendationStatusTypeEnum.STOPPED]:
        filters.data?.counts.workloadIsStopped ?? 0,
      [LOW_CONFIDENCE_STATUS_TYPE]:
        filters.data?.counts.recommendationIsLowConfidence ?? 0,
      [ERROR_STATUS_TYPE]: filters.data?.counts.workloadHasError ?? 0,
    };
  }, [filters.data]);

  const presentedStatuses = Object.entries(statusesCount).reduce(
    (acc, [status, count]) => {
      if (count > 0) {
        acc.push(status as FilterableStatuses);
      }
      return acc;
    },
    [] as FilterableStatuses[]
  );

  return (
    <Autocomplete
      testId="status-filter"
      inputText="Status"
      options={presentedStatuses}
      value={value}
      onChange={(value) => {
        if (value?.length) {
          setValue?.(value);
        } else {
          clear?.();
        }
      }}
      multiple
      checkable
      applySortingWhileSearching={false}
      listSx={{ '& .MuiListItemText-primary': { whiteSpace: 'nowrap' } }}
      renderOption={(status, itemProps) => {
        const Icon = STATUS_ICON_MAP[status];
        return (
          <ListItem {...itemProps}>
            <StatusChip
              sx={{
                ...STATUS_BADGE_COLOR_MAP[status],
                width: '100%',
                '& .DsChip-Content': { width: '100%' },
              }}
              size="md"
            >
              <Box display="flex" flex={1} justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={4}>
                  <Icon weight="bold" />
                  <span>{STATUS_DISPLAY_NAME_MAP[status]}</span>
                </Box>
                <span>({statusesCount[status]})</span>
              </Box>
            </StatusChip>
          </ListItem>
        );
      }}
      size="sm"
    />
  );
};
